import { Link, useNavigate } from "react-router-dom";
import { makeTr } from "@/i18n/tr";
import { useTranslation } from "react-i18next";
import { useStore } from "@/store/useStore";
import { Button } from "@/components/ui/button";
import { 
  Crown, 
  Shield, 
  Zap, 
  TrendingUp,
  Globe,
  ChevronRight,
  Users,
  Gamepad2,
  Trophy,
  Smartphone
} from "lucide-react";
import { useEffect } from "react";

export default function LandingPage() {
  const { t, i18n } = useTranslation();
  const tr = makeTr(i18n.language);
  const navigate = useNavigate();
  const { isAuthenticated, setLanguage, language } = useStore();

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/lobby");
    }
  }, [isAuthenticated, navigate]);

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    i18n.changeLanguage(lang);
  };

  const features = [
    {
      icon: Shield,
      title: t("landing.features.secure"),
      description: t("landing.features.secureDesc"),
    },
    {
      icon: Zap,
      title: t("landing.features.instant"),
      description: t("landing.features.instantDesc"),
    },
    {
      icon: TrendingUp,
      title: t("landing.features.rated"),
      description: t("landing.features.ratedDesc"),
    },
  ];

  const stats = [
    { value: "10", label: t("landing.stats.players"), icon: Gamepad2 },
    { value: t("landing.stats.top3"), label: t("landing.stats.prize"), icon: Trophy },
    { value: "24/7", label: t("landing.stats.games"), icon: Users },
  ];

  return (
    <div className="min-h-screen bg-background noise-bg">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Crown className="h-8 w-8 text-primary" />
            <span className="text-xl font-black tracking-tight">
              TEA<span className="text-primary">CHESS</span>
            </span>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Language Toggle */}
            <div className="flex items-center gap-1 bg-muted/50 rounded-full p-1">
              <button
                onClick={() => handleLanguageChange("en")}
                data-testid="landing-lang-en"
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors
                  ${language === "en" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                EN
              </button>
              <button
                onClick={() => handleLanguageChange("ru")}
                data-testid="landing-lang-ru"
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors
                  ${language === "ru" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                RU
              </button>
              <button
                onClick={() => handleLanguageChange("zh")}
                data-testid="landing-lang-zh"
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors
                  ${language === "zh" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                中文
              </button>
            </div>

            <Link to="/login">
              <Button variant="ghost" className="font-medium" data-testid="login-btn">
                {t("nav.login")}
              </Button>
            </Link>
            <Link to="/register">
              <Button className="btn-gold" data-testid="register-btn">
                {t("nav.register")}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        {/* Background image with overlay */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(9, 9, 11, 0.85) 0%, rgba(9, 9, 11, 0.95) 100%), 
                              url('https://images.unsplash.com/photo-1528819622765-d6bcf132f793?w=1920&q=80')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        
        {/* Content */}
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Text content */}
            <div className="lg:col-span-7 space-y-8">
              <div className="space-y-4">
                <h1 className="text-5xl md:text-7xl font-black tracking-tight leading-none animate-fade-in">
                  {t("landing.title")}
                  <br />
                  <span className="text-primary">{t("landing.subtitle")}</span>
                </h1>
                <p className="text-lg md:text-xl text-muted-foreground max-w-xl animate-slide-up" style={{ animationDelay: "0.1s" }}>
                  {t("landing.description")}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 animate-slide-up" style={{ animationDelay: "0.2s" }}>
                <Link to="/register">
                  <Button size="lg" className="btn-gold text-lg px-8 py-6 w-full sm:w-auto" data-testid="cta-btn">
                    {t("landing.cta")}
                    <ChevronRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <a href="/downloads/TeaChess.apk" download data-testid="android-download-btn">
                  <Button size="lg" variant="outline" className="text-lg px-8 py-6 w-full sm:w-auto border-primary/60 text-foreground hover:bg-primary/10">
                    <Smartphone className="mr-2 h-5 w-5" />
                    {t("landing.android")}
                  </Button>
                </a>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 pt-8 animate-slide-up" style={{ animationDelay: "0.3s" }}>
                {stats.map((stat, index) => (
                  <div key={index} className="stats-card rounded-lg p-4 text-center">
                    <stat.icon className="h-5 w-5 text-primary mx-auto mb-2" />
                    <div className="font-accent text-2xl md:text-3xl font-bold text-primary">
                      {stat.value}
                    </div>
                    <div className="text-xs md:text-sm text-muted-foreground">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Chess board visual */}
            <div className="lg:col-span-5 hidden lg:block">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-secondary/20 blur-3xl" />
                <img 
                  src="https://images.unsplash.com/photo-1523875194681-bedd468c58bf?w=600&q=80"
                  alt="Chess"
                  className="relative rounded-lg border border-border/50 shadow-2xl animate-fade-in"
                  style={{ animationDelay: "0.4s" }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-32 border-t border-border/50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="feature-card glass rounded-lg p-8 card-interactive animate-slide-up"
                style={{ animationDelay: `${0.1 * index}s` }}
              >
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-6">
                  <feature.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 border-t border-border/50">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-2xl mx-auto space-y-8">
            <Crown className="h-16 w-16 text-primary mx-auto animate-pulse-slow" />
            <h2 className="text-4xl md:text-5xl font-black tracking-tight">
              {tr("Готов к игре?", "Ready to Play?")}
            </h2>
            <p className="text-lg text-muted-foreground">
              {language === "ru" 
                ? "Присоединяйся к турнирам TeaChess уже сегодня"
                : "Join TeaChess tournaments today"}
            </p>
            <Link to="/register">
              <Button size="lg" className="btn-gold text-lg px-12 py-6">
                {t("landing.cta")}
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/50 py-8">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Crown className="h-6 w-6 text-primary" />
              <span className="font-bold">TEACHESS</span>
            </div>
            <div className="text-sm text-muted-foreground">
              © 2025 TeaChess. {tr("Все права защищены.", "All rights reserved.")}
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <Globe className="h-4 w-4" />
              <button 
                onClick={() => handleLanguageChange("en")}
                className={language === "en" ? "text-primary" : "hover:text-foreground"}
              >
                English
              </button>
              <span>/</span>
              <button 
                onClick={() => handleLanguageChange("ru")}
                className={language === "ru" ? "text-primary" : "hover:text-foreground"}
              >
                Русский
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
